import { useMergeRefs } from "@chakra-ui/react";
import React from "react";
import { useController } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { NumericFormatProps } from "react-number-format/types/types";
import { usePrefixedFieldName } from "shared/components/form/FormFieldPrefix";
import { useIsFormDisabled } from "shared/components/form/FormStateProvider";
import { DEFAULT_DECIMAL_SCALE } from "shared/components/input/InputConstants";
import { makeInputTransforms } from "shared/components/input/text/InputUtils";
import {
  SimpleTextInput,
  SimpleTextInputProps,
} from "shared/components/input/text/SimpleTextInput";
import { PercentIcon } from "shared/icons/PercentIcon";
import { FormInputValue } from "shared/types/FormInputValue";
import { getFieldError } from "shared/utils/FormUtils";

type SimpleNumberInputValueType = "decimal" | "percent" | "number";

export type SimpleNumberInputProps = NumericFormatProps<
  {
    valueType?: SimpleNumberInputValueType;
  } & Omit<SimpleTextInputProps, "type">
>;

export type FormSimpleNumberInputProps = {
  name: string;
  shouldUnregister?: boolean;
  onChangeValue?: (value: string | number) => void;
} & Omit<
  SimpleNumberInputProps,
  "name" | "value" | "error" | "onChange" | "onChangeValue"
>;

const defaultPropsForValueType: Partial<
  Record<SimpleNumberInputValueType, Partial<SimpleNumberInputProps>>
> = {
  percent: {
    isAllowed: ({ floatValue }) =>
      !floatValue || (floatValue >= 0 && floatValue <= 100),
    allowNegative: false,
  },
};

const rightAddons: Partial<
  Record<SimpleNumberInputValueType, React.ReactNode>
> = {
  percent: <PercentIcon width="24px" height="24px" color="gray.400" />,
};

export const SimpleNumberInput = React.forwardRef<
  HTMLInputElement,
  SimpleNumberInputProps
>(
  (
    { valueType = "number", decimalScale = DEFAULT_DECIMAL_SCALE, ...props },
    ref,
  ) => {
    const defaultProps = defaultPropsForValueType[valueType];
    return (
      <NumericFormat<SimpleTextInputProps>
        getInputRef={ref}
        customInput={SimpleTextInput}
        decimalScale={decimalScale}
        inputRightElement={
          valueType === "percent" ? rightAddons[valueType] : undefined
        }
        {...defaultProps}
        {...props}
      />
    );
  },
);
SimpleNumberInput.displayName = "SimpleNumberInput";

export const FormSimpleNumberInput = React.forwardRef<
  HTMLInputElement,
  FormSimpleNumberInputProps
>(
  (
    {
      name,
      isDisabled,
      defaultValue,
      shouldUnregister = true,
      onChangeValue,
      ...props
    },
    fwRef,
  ) => {
    const prefixedName = usePrefixedFieldName(name);
    const {
      field: { ref: fieldRef, value, onChange, ...field },
      formState: { errors },
    } = useController<{ text: FormInputValue }>({
      name: prefixedName as "text",
      shouldUnregister,
      defaultValue: defaultValue || "",
    });
    const mergedRef = useMergeRefs(fwRef, fieldRef);
    const isFormDisabled = useIsFormDisabled();
    const { transformInput, transformOutput } = makeInputTransforms(
      props.valueType === "number",
      props.valueType === "percent",
      true,
    );
    return (
      <SimpleNumberInput
        {...field}
        {...props}
        value={transformInput(value)}
        onChange={(e) => {
          const output = transformOutput(e.target.value);
          onChange(output);
          onChangeValue?.(output);
        }}
        isDisabled={isDisabled || isFormDisabled}
        error={getFieldError(errors, prefixedName)?.message}
        ref={mergedRef}
        textAlign="right"
        _placeholder={{
          textAlign: "right",
          color: "gray.400",
        }}
      />
    );
  },
);
FormSimpleNumberInput.displayName = "FormSimpleNumberInput";
